import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";

const fmt = (n, d = 2) => n == null || n === 0 ? "—" : Number(n).toFixed(d);

/* Map AngelOne orderstatus → display label + colour */
const STATUS_MAP = {
  open:        { label: "OPEN",      bg: "#fff3cd", color: "#856404" },
  "open pending": { label: "PENDING", bg: "#fff3cd", color: "#856404" },
  pending:     { label: "PENDING",   bg: "#fff3cd", color: "#856404" },
  complete:    { label: "FILLED",    bg: "#d4edda", color: "#155724" },
  filled:      { label: "FILLED",    bg: "#d4edda", color: "#155724" },
  cancelled:   { label: "CANCELLED", bg: "#f8d7da", color: "#721c24" },
  rejected:    { label: "REJECTED",  bg: "#f8d7da", color: "#721c24" },
  modified:    { label: "MODIFIED",  bg: "#cce5ff", color: "#004085" },
  "trigger pending": { label: "TRIGGER PENDING", bg: "#e2d9f3", color: "#4a0080" },
};

const statusStyle = (raw) => {
  const key = (raw || "").toLowerCase();
  const s = STATUS_MAP[key] || { label: raw || "—", bg: "#e9ecef", color: "#555" };
  return { label: s.label, style: { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color } };
};

const isModifiable = (raw) => {
  const key = (raw || "").toLowerCase();
  return key === "open" || key === "open pending" || key === "pending" || key === "trigger pending";
};

/* ── Live Orders (from broker) ── */
const LiveOrders = ({ account }) => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [editing, setEditing] = useState(null);   // orderid being edited
  const [newPrice, setNewPrice] = useState("");
  const [opMsg, setOpMsg]     = useState({});      // { orderid: message }

  const fetchLive = useCallback(async () => {
    if (!account || account === "All Accounts") return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get("/broker/live-orders", { params: { username: account } });
      setOrders(r.data.orders || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => { fetchLive(); }, [fetchLive]);

  const startEdit = (o) => {
    setEditing(o.orderid);
    setNewPrice(o.price || "");
    setOpMsg(m => ({ ...m, [o.orderid]: null }));
  };

  const submitModify = async (o) => {
    try {
      await axios.post("/broker/modify-order", {
        username:      account,
        orderid:       o.orderid,
        variety:       o.variety   || "NORMAL",
        ordertype:     "LIMIT",
        producttype:   o.producttype,
        exchange:      o.exchange,
        tradingsymbol: o.tradingsymbol,
        symboltoken:   o.symboltoken,
        quantity:      o.quantity,
        price:         parseFloat(newPrice),
        duration:      o.duration || "DAY",
      });
      setOpMsg(m => ({ ...m, [o.orderid]: { ok: true, text: "Modified successfully" } }));
      setEditing(null);
      setTimeout(fetchLive, 1500);
    } catch (e) {
      setOpMsg(m => ({ ...m, [o.orderid]: { ok: false, text: e.response?.data?.error || e.message } }));
    }
  };

  const cancelOrder = async (o) => {
    if (!window.confirm(`Cancel order ${o.orderid} for ${o.tradingsymbol}?`)) return;
    try {
      await axios.post("/broker/cancel-order", { username: account, orderid: o.orderid, variety: o.variety || "NORMAL" });
      setOpMsg(m => ({ ...m, [o.orderid]: { ok: true, text: "Cancelled" } }));
      setTimeout(fetchLive, 1500);
    } catch (e) {
      setOpMsg(m => ({ ...m, [o.orderid]: { ok: false, text: e.response?.data?.error || e.message } }));
    }
  };

  if (!account || account === "All Accounts") return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#2c3e50" }}>Today's Live Orders</h3>
        <button onClick={fetchLive} style={btnSecondary} disabled={loading}>
          {loading ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      {error && <div style={{ color: "#dc3545", fontSize: 13, marginBottom: 8 }}>⚠️ {error}</div>}

      <div style={tableWrap}>
        {loading ? (
          <div style={centered}>Loading…</div>
        ) : orders.length === 0 ? (
          <div style={centered}>No orders today.</div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                {["Symbol", "Side", "Type", "Qty", "Order Price", "Avg Price", "Status", "Time", "Actions"].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => {
                const { label, style: sBadge } = statusStyle(o.orderstatus);
                const modifiable = isModifiable(o.orderstatus);
                const isEditingThis = editing === o.orderid;
                const msg = opMsg[o.orderid];
                return (
                  <React.Fragment key={o.orderid || i}>
                    <tr style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={td}><strong style={{ fontSize: 12 }}>{o.tradingsymbol}</strong></td>
                      <td style={td}>
                        <span style={{ ...sideBadge, background: o.transactiontype === "BUY" ? "#d4edda" : "#fde2e2", color: o.transactiontype === "BUY" ? "#155724" : "#721c24" }}>
                          {o.transactiontype}
                        </span>
                      </td>
                      <td style={{ ...td, fontSize: 12, color: "#555" }}>{o.ordertype || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {o.filledshares > 0
                          ? <span>{o.filledshares}<span style={{ color: "#aaa", fontSize: 11 }}>/{o.quantity}</span></span>
                          : o.quantity}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {isEditingThis ? (
                          <input
                            type="number" value={newPrice}
                            onChange={e => setNewPrice(e.target.value)}
                            style={{ width: 80, padding: "3px 6px", borderRadius: 4, border: "1px solid #007bff", fontSize: 13 }}
                            autoFocus
                          />
                        ) : (
                          <span style={o.price ? { color: "#0055aa", fontWeight: 600 } : { color: "#888" }}>
                            {o.price ? `$${fmt(o.price)}` : "MARKET"}
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>
                        {o.averageprice ? `$${fmt(o.averageprice)}` : "—"}
                      </td>
                      <td style={td}><span style={sBadge}>{label}</span></td>
                      <td style={{ ...td, fontSize: 11, color: "#888" }}>
                        {(o.updatetime || o.exchorderupdatetime || "").slice(0, 16)}
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {modifiable && (
                          isEditingThis ? (
                            <span style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => submitModify(o)} style={btnSave}>Save</button>
                              <button onClick={() => setEditing(null)} style={btnCancel}>✕</button>
                            </span>
                          ) : (
                            <span style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => startEdit(o)} style={btnEdit}>Edit</button>
                              <button onClick={() => cancelOrder(o)} style={btnCancelOrd}>Cancel</button>
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                    {msg && (
                      <tr style={{ background: msg.ok ? "#f0fff4" : "#fff0f0" }}>
                        <td colSpan={9} style={{ padding: "4px 12px", fontSize: 12, color: msg.ok ? "#155724" : "#dc3545" }}>
                          {msg.ok ? "✓" : "⚠️"} {msg.text}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ── Order History (from order_log) ── */
export default function OrderHistory() {
  const { selectedAccount } = useAccount();

  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]     = useState(0);

  const PAGE_SIZE = 100;

  const [filters, setFilters] = useState({
    symbol: "", side: "", pe_ce: "", date_from: "", date_to: ""
  });
  const [applied, setApplied] = useState(filters);

  useEffect(() => {
    if (selectedAccount) fetchOrders(0, applied);
  }, [selectedAccount]);

  const fetchOrders = async (pageNum, f) => {
    setLoading(true);
    try {
      const res = await axios.get("/trading/order-history", {
        params: {
          username: selectedAccount,
          symbol:    f.symbol,
          side:      f.side,
          pe_ce:     f.pe_ce,
          date_from: f.date_from,
          date_to:   f.date_to,
          limit:     PAGE_SIZE,
          offset:    pageNum * PAGE_SIZE
        }
      });
      setRows(res.data.rows);
      setTotal(res.data.total);
      setPage(pageNum);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => { setApplied(filters); fetchOrders(0, filters); };
  const clearFilters = () => {
    const empty = { symbol: "", side: "", pe_ce: "", date_from: "", date_to: "" };
    setFilters(empty); setApplied(empty); fetchOrders(0, empty);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ padding: "20px 25px 30px", background: "#f4f6f9", minHeight: "100vh" }}>

      <div style={{ marginBottom: 20 }}>
        <h2 style={pageTitle}>Orders</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          {selectedAccount} — live broker orders + order history
        </p>
      </div>

      {/* Live Orders */}
      <LiveOrders account={selectedAccount} />

      <div style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#2c3e50", margin: "0 0 10px" }}>
          Order History
          <span style={{ fontWeight: 400, fontSize: 13, color: "#888", marginLeft: 8 }}>
            {total.toLocaleString("en-US")} records
          </span>
        </h3>
      </div>

      {/* Filters */}
      <div style={filterCard}>
        <div style={filterGrid}>
          <div>
            <label style={filterLabel}>Symbol</label>
            <input placeholder="e.g. NIFTY" value={filters.symbol}
              onChange={e => setFilters(f => ({ ...f, symbol: e.target.value }))} style={filterInput} />
          </div>
          <div>
            <label style={filterLabel}>Side</label>
            <select value={filters.side} onChange={e => setFilters(f => ({ ...f, side: e.target.value }))} style={filterSelect}>
              <option value="">All</option>
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
            </select>
          </div>
          <div>
            <label style={filterLabel}>Type</label>
            <select value={filters.pe_ce} onChange={e => setFilters(f => ({ ...f, pe_ce: e.target.value }))} style={filterSelect}>
              <option value="">All</option>
              <option value="CE">CE (Call)</option>
              <option value="PE">PE (Put)</option>
            </select>
          </div>
          <div>
            <label style={filterLabel}>From Date</label>
            <input type="date" value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} style={filterInput} />
          </div>
          <div>
            <label style={filterLabel}>To Date</label>
            <input type="date" value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} style={filterInput} />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <button onClick={applyFilters} style={btnPrimary}>Apply</button>
            <button onClick={clearFilters} style={btnSecondary}>Clear</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={tableWrap}>
        {loading ? (
          <div style={centered}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={centered}>No orders found.</div>
        ) : (
          <>
            <table style={table}>
              <thead>
                <tr>
                  {["#", "Symbol", "Side", "Type", "Lots", "Qty", "Order Price", "Avg Price", "Status", "Broker", "Date"].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const { label, style: sBadge } = statusStyle(r.orderstatus);
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={{ ...td, color: "#aaa", fontSize: 11 }}>{r.id}</td>
                      <td style={td}><strong style={{ fontSize: 12 }}>{r.tradingsymbol}</strong></td>
                      <td style={td}>
                        <span style={{ ...sideBadge, background: r.transactiontype === "BUY" ? "#d4edda" : "#fde2e2", color: r.transactiontype === "BUY" ? "#155724" : "#721c24" }}>
                          {r.transactiontype}
                        </span>
                      </td>
                      <td style={td}>
                        {r.pe_ce ? (
                          <span style={{ ...typeBadge, background: r.pe_ce === "CE" ? "#e8f4fd" : "#fef3f3", color: r.pe_ce === "CE" ? "#0069a8" : "#a80000" }}>
                            {r.pe_ce}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>{r.lots}</td>
                      <td style={{ ...td, textAlign: "right" }}>{r.quantity?.toLocaleString("en-US")}</td>
                      <td style={{ ...td, textAlign: "right" }}>{fmt(r.orderprice)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(r.averageprice)}</td>
                      <td style={td}><span style={sBadge}>{label}</span></td>
                      <td style={{ ...td, fontSize: 12, color: "#888" }}>{r.broker}</td>
                      <td style={{ ...td, fontSize: 12, color: "#555" }}>{r.created_at?.slice(0, 16)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={pagination}>
                <span style={{ color: "#888", fontSize: 13 }}>
                  Page {page + 1} of {totalPages} ({total.toLocaleString("en-US")} orders)
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => fetchOrders(page - 1, applied)} disabled={page === 0} style={pageBtn}>← Prev</button>
                  <button onClick={() => fetchOrders(page + 1, applied)} disabled={page >= totalPages - 1} style={pageBtn}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* styles */
const pageTitle    = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const filterCard   = { background: "#fff", borderRadius: 12, padding: "16px 20px", marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const filterGrid   = { display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.5fr 1.5fr auto", gap: 12, alignItems: "end" };
const filterLabel  = { display: "block", fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", marginBottom: 4 };
const filterInput  = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%" };
const filterSelect = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%" };
const btnPrimary   = { padding: "8px 16px", borderRadius: 6, border: "none", background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 13 };
const tableWrap    = { background: "#fff", borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "auto" };
const table        = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th           = { padding: "10px 12px", background: "#f8f9fa", borderBottom: "2px solid #dee2e6", fontWeight: 600, fontSize: 11, color: "#6c757d", textTransform: "uppercase", whiteSpace: "nowrap" };
const td           = { padding: "9px 12px", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap", verticalAlign: "middle" };
const centered     = { padding: 40, textAlign: "center", color: "#888" };
const sideBadge    = { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700 };
const typeBadge    = { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700 };
const pagination   = { padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid #f0f0f0" };
const pageBtn      = { padding: "6px 12px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 13 };
const btnEdit      = { padding: "3px 10px", borderRadius: 4, border: "1px solid #007bff", background: "#fff", color: "#007bff", cursor: "pointer", fontSize: 12, fontWeight: 600 };
const btnSave      = { padding: "3px 10px", borderRadius: 4, border: "none", background: "#28a745", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 };
const btnCancel    = { padding: "3px 8px", borderRadius: 4, border: "1px solid #ccc", background: "#fff", color: "#555", cursor: "pointer", fontSize: 12 };
const btnCancelOrd = { padding: "3px 10px", borderRadius: 4, border: "1px solid #dc3545", background: "#fff", color: "#dc3545", cursor: "pointer", fontSize: 12, fontWeight: 600 };
